// src/services/configService.js

import realApiService from './api';
import mockApiService from './mockApiService';

// ─── Mock toggle ──────────────────────────────────────────────────────────────
// Flip REACT_APP_USE_MOCK_API=true in .env to use mock data everywhere.
const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === 'true';
const apiService = USE_MOCK ? mockApiService : realApiService;

class ConfigurationService {
  constructor() {
    this.cache = {
      configurations: null,
      lastFetch: null,
      pageConfigurations: null,
      pageConfigsLastFetch: null,
    };
    this.cacheDuration = 5 * 60 * 1000;
  }

  // ─── Core CRUD ──────────────────────────────────────────────────────────────

  async getAll(filters = {}) {
    const now = Date.now();

    if (
      this.cache.configurations &&
      this.cache.lastFetch &&
      now - this.cache.lastFetch < this.cacheDuration
    ) {
      return this.cache.configurations;
    }

    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);

      const url = `/configurations${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiService.get(url);

      this.cache.configurations = response;
      this.cache.lastFetch = now;

      return response;
    } catch (error) {
      console.error('❌ Failed to fetch configurations:', error);
      throw error;
    }
  }

  async get(key) {
    try {
      return await apiService.get(`/configurations/${key}`);
    } catch (error) {
      console.error(`❌ Failed to get configuration ${key}:`, error);
      throw error;
    }
  }

  async update(key, value, metadata = {}) {
    try {
      const response = await apiService.put(`/configurations/${key}`, { value, metadata });
      this.clearCache();
      return response;
    } catch (error) {
      console.error(`❌ Failed to update configuration ${key}:`, error);
      throw error;
    }
  }

  async create(key, value, metadata = {}) {
    try {
      const response = await apiService.post('/configurations', { key, value, metadata });
      this.clearCache();
      return response;
    } catch (error) {
      console.error(`❌ Failed to create configuration ${key}:`, error);
      throw error;
    }
  }

  async delete(key) {
    try {
      const response = await apiService.delete(`/configurations/${key}`);
      this.clearCache();
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete configuration ${key}:`, error);
      throw error;
    }
  }

  async backup() {
    try {
      return await apiService.post('/configurations/backup');
    } catch (error) {
      console.error('❌ Failed to backup configurations:', error);
      throw error;
    }
  }

  async healthCheck() {
    try {
      return await apiService.get('/configurations/health');
    } catch (error) {
      console.error('❌ Configuration health check failed:', error);
      return { status: 'error', message: error.message };
    }
  }

  // ─── Page / JSON configuration methods ──────────────────────────────────────

  async getAvailablePages() {
    try {
      return await apiService.get('/configurations/pages');
    } catch (error) {
      console.error('❌ Failed to fetch available pages:', error);
      throw error;
    }
  }

  async getPageConfiguration(page, category) {
    try {
      return await apiService.get(`/configurations/pages/${page}/${category}`);
    } catch (error) {
      console.error(`❌ Failed to fetch page configuration ${page}/${category}:`, error);
      throw error;
    }
  }

  async getAllPageConfigurations() {
    const now = Date.now();

    if (
      this.cache.pageConfigurations &&
      this.cache.pageConfigsLastFetch &&
      now - this.cache.pageConfigsLastFetch < this.cacheDuration
    ) {
      return this.cache.pageConfigurations;
    }

    try {
      const pagesResponse = await this.getAvailablePages();
      const pageConfigs = {};

      if (pagesResponse.success && pagesResponse.pages) {
        for (const pageInfo of pagesResponse.pages) {
          try {
            const configResponse = await this.getPageConfiguration(
              pageInfo.page,
              pageInfo.category,
            );
            if (configResponse.success) {
              const configKey = `${pageInfo.page}_${pageInfo.category}`;
              pageConfigs[configKey] = {
                page: pageInfo.page,
                category: pageInfo.category,
                data: configResponse.data || configResponse.configurations || {},
                configCount: Object.keys(
                  configResponse.data || configResponse.configurations || {},
                ).length,
              };
            }
          } catch (err) {
            console.warn(`⚠️ Failed to load ${pageInfo.page}/${pageInfo.category}:`, err.message);
          }
        }
      }

      this.cache.pageConfigurations = pageConfigs;
      this.cache.pageConfigsLastFetch = now;
      return pageConfigs;
    } catch (error) {
      console.error('❌ Failed to load page configurations:', error);
      return {};
    }
  }

  async uploadJsonConfiguration(jsonConfig) {
    try {
      const validation = this.validateJsonConfig(jsonConfig);
      if (!validation.isValid) {
        throw new Error(`Invalid JSON configuration: ${validation.errors.join(', ')}`);
      }
      const response = await apiService.post('/configurations/upload-json', jsonConfig);
      this.clearCache();
      return response;
    } catch (error) {
      console.error('❌ Failed to upload JSON configuration:', error);
      throw error;
    }
  }

  async syncJsonConfiguration(jsonConfig) {
    const { page, category, data } = jsonConfig;
    const results = { created: [], updated: [], errors: [] };

    for (const [key, config] of Object.entries(data)) {
      try {
        const metadata = {
          ...config.metadata,
          page,
          category,
          synced_at: new Date().toISOString(),
        };

        try {
          await this.get(key);
          await this.update(key, config.value, metadata);
          results.updated.push(key);
        } catch {
          await this.create(key, config.value, metadata);
          results.created.push(key);
        }
      } catch (error) {
        results.errors.push({ key, error: error.message });
      }
    }

    return results;
  }

  async exportPageConfiguration(page, category) {
    try {
      return await apiService.get(`/configurations/export/${page}/${category}`);
    } catch (error) {
      console.error(`❌ Failed to export configuration ${page}/${category}:`, error);
      throw error;
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  async getTooltips() {
    return this.getAll({ category: 'tooltips' });
  }

  async updateTooltip(tooltipKey, tooltipText) {
    const key = tooltipKey.startsWith('tooltip_') ? tooltipKey : `tooltip_${tooltipKey}`;
    return this.update(key, tooltipText, {
      category: 'tooltips',
      type: 'string',
      description: `Tooltip for ${key.replace('tooltip_', '').replace(/_/g, ' ')}`,
    });
  }

  validateJsonConfig(jsonConfig) {
    const errors = [];
    const warnings = [];

    if (!jsonConfig.page) errors.push('Missing required field: page');
    if (!jsonConfig.category) errors.push('Missing required field: category');
    if (!jsonConfig.data || typeof jsonConfig.data !== 'object') {
      errors.push('Missing or invalid data field');
    } else {
      Object.entries(jsonConfig.data).forEach(([key, config]) => {
        if (!config.value && config.value !== false && config.value !== 0)
          warnings.push(`Configuration ${key} has empty value`);
        if (!config.metadata) {
          warnings.push(`Configuration ${key} missing metadata`);
        } else {
          if (!config.metadata.type) warnings.push(`Configuration ${key} missing type`);
          if (!config.metadata.description) warnings.push(`Configuration ${key} missing description`);
        }
      });
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  // ─── Cache management ─────────────────────────────────────────────────────────

  clearCache() {
    this.cache = {
      configurations: null,
      lastFetch: null,
      pageConfigurations: null,
      pageConfigsLastFetch: null,
    };
  }

  isCacheValid() {
    return !!(
      this.cache.configurations &&
      this.cache.lastFetch &&
      Date.now() - this.cache.lastFetch < this.cacheDuration
    );
  }

  getCacheInfo() {
    return {
      hasCachedData: !!this.cache.configurations,
      lastFetch: this.cache.lastFetch,
      cacheAge: this.cache.lastFetch ? Date.now() - this.cache.lastFetch : null,
      isValid: this.isCacheValid(),
    };
  }
}

const configService = new ConfigurationService();
export default configService;